"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { Gift, Clock, Copy, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Image from "next/image";
import { useMounted } from "@/hooks/use-mounted";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GET_OFFER, CategoryType } from "@/graphql/queries/get-offer";
import { useParams } from "next/navigation";
import { useCountry } from "@/hooks/use-country";
interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}
const calculateTimeLeft = (endDateStr: string): TimeLeft | null => {
  const difference = +new Date(endDateStr) - +new Date();
  if (difference <= 0) return null;
  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor(difference / (1000 * 60 * 60) % 24),
    minutes: Math.floor(difference / 1000 / 60 % 60),
    seconds: Math.floor(difference / 1000 % 60)
  };
};
export default function OfferPopup() {
  const $ = _c(80);
  const mounted = useMounted();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const {
    course
  } = useParams();
  const country = useCountry();
  const t0 = course ?? null;
  let t1;
  if ($[0] !== country || $[1] !== t0) {
    t1 = {
      variables: {
        type: CategoryType.ALL_COUNTRIES_ONLY_COUPON,
        pageSlug: t0,
        countryCode: country
      },
      fetchPolicy: "cache-first"
    };
    $[0] = country;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data,
    loading,
    error
  } = useQuery(GET_OFFER, t1);
  const offer = data?.offerByCategory;
  const [timeLeft, setTimeLeft] = useState(null);
  let t2;
  if ($[3] !== offer) {
    t2 = () => {
      if (!offer?.endDate) {
        return;
      }
      const timeout = setTimeout(() => {
        setTimeLeft(calculateTimeLeft(offer.endDate));
      }, 0);
      const timer = setInterval(() => {
        const remaining = calculateTimeLeft(offer.endDate);
        setTimeLeft(remaining);
        if (!remaining) {
          clearInterval(timer);
        }
      }, 1000);
      return () => {
        clearTimeout(timeout);
        clearInterval(timer);
      };
    };
    $[3] = offer;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = offer?.endDate;
  let t4;
  if ($[5] !== t3) {
    t4 = [t3];
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t2, t4);
  let t5;
  if ($[7] !== error || $[8] !== loading || $[9] !== mounted || $[10] !== offer) {
    t5 = () => {
      if (!mounted || loading || error || !offer) {
        return;
      }
      const timeRemaining = calculateTimeLeft(offer.endDate);
      if (!timeRemaining) {
        return;
      }
      const closedTime = localStorage.getItem("spoclearn-offer-closed-time");
      const isRecentlyClosed = closedTime && Date.now() - parseInt(closedTime, 10) < 86400000;
      if (!isRecentlyClosed) {
        const openTimer = setTimeout(() => {
          setOpen(true);
        }, 2000);
        return () => clearTimeout(openTimer);
      }
    };
    $[7] = error;
    $[8] = loading;
    $[9] = mounted;
    $[10] = offer;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = offer?.endDate;
  let t7;
  if ($[12] !== error || $[13] !== loading || $[14] !== mounted || $[15] !== offer || $[16] !== t6) {
    t7 = [mounted, loading, error, offer, t6];
    $[12] = error;
    $[13] = loading;
    $[14] = mounted;
    $[15] = offer;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useEffect(t5, t7);
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = nextOpen => {
      if (!nextOpen) {
        localStorage.setItem("spoclearn-offer-closed-time", Date.now().toString());
        setOpen(false);
      } else {
        setOpen(true);
      }
    };
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleOpenChange = t8;
  let t9;
  if ($[19] !== offer) {
    t9 = async () => {
      if (!offer?.couponCode) {
        return;
      }
      try {
        await navigator.clipboard.writeText(offer.couponCode);
        setCopied(true);
        toast.success("Coupon code copied!", {
          description: `Use code ${offer.couponCode} at checkout to claim your offer.`
        });
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error("Failed to copy code.");
      }
    };
    $[19] = offer;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handleCopyCode = t9;
  if (!mounted || !offer || !timeLeft) {
    return null;
  }
  const hasBanner = Boolean(offer.banner);
  let t10;
  if ($[21] !== hasBanner || $[22] !== offer.banner || $[23] !== offer.name) {
    t10 = hasBanner ? <div className="bg-muted relative h-48 w-full overflow-hidden"><Image src={offer.banner} alt={offer.name} width={460} height={192} unoptimized={true} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" /></div> : <div className="relative flex flex-col items-center justify-center bg-linear-to-b from-amber-500/5 to-transparent pt-8 pb-4 dark:from-amber-500/10"><div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-amber-500/20 bg-amber-500/10 text-amber-600 shadow-xs dark:border-amber-500/30 dark:bg-amber-500/20 dark:text-amber-400"><Gift className="h-8 w-8 stroke-[1.5]" /><span className="absolute -top-1 -right-1 flex h-3.5 w-3.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75" /><span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-amber-500" /></span></div><div className="mt-4 flex items-center gap-1 rounded-full border border-amber-500/20 bg-amber-500/10 px-3 py-1 text-amber-600 dark:border-amber-500/30 dark:bg-amber-500/20 dark:text-amber-400"><Sparkles className="size-3 fill-amber-600/20 text-amber-600 dark:fill-amber-400/20 dark:text-amber-400" /><span className="text-[10px] font-extrabold tracking-wider uppercase">Limited Time Promotion</span></div></div>;
    $[21] = hasBanner;
    $[22] = offer.banner;
    $[23] = offer.name;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== offer.name) {
    t11 = <DialogTitle className="text-foreground text-2xl font-bold tracking-tight">{offer.name}</DialogTitle>;
    $[25] = offer.name;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== offer.description) {
    t12 = <DialogDescription className="text-muted-foreground mt-2 text-sm leading-relaxed">{offer.description}</DialogDescription>;
    $[27] = offer.description;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t11 || $[30] !== t12) {
    t13 = <DialogHeader className="text-center">{t11}{t12}</DialogHeader>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="text-muted-foreground mb-3 flex items-center justify-center gap-1.5 text-[11px] font-bold tracking-widest uppercase dark:text-neutral-400"><Clock className="text-primary dark:text-primary size-3.5 animate-pulse" /><span>Offer Expires In</span></div>;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const t15 = String(timeLeft.days);
  let t16;
  if ($[33] !== t15) {
    t16 = t15.padStart(2, "0");
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t16) {
    t17 = <span className="text-foreground font-mono text-xl leading-none font-extrabold">{t16}</span>;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className="text-muted-foreground mt-1.5 text-[9px] font-bold tracking-widest uppercase dark:text-neutral-500">Days</span>;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t17) {
    t19 = <div className="bg-background border-border/80 flex min-w-14.5 flex-col items-center rounded-xl border px-3 py-2 shadow-xs dark:border-neutral-800/80 dark:bg-neutral-950">{t17}{t18}</div>;
    $[38] = t17;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  const t20 = String(timeLeft.hours);
  let t21;
  if ($[40] !== t20) {
    t21 = t20.padStart(2, "0");
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t21) {
    t22 = <span className="text-foreground font-mono text-xl leading-none font-extrabold">{t21}</span>;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <span className="text-muted-foreground mt-1.5 text-[9px] font-bold tracking-widest uppercase dark:text-neutral-500">Hrs</span>;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t22) {
    t24 = <div className="bg-background border-border/80 flex min-w-14.5 flex-col items-center rounded-xl border px-3 py-2 shadow-xs dark:border-neutral-800/80 dark:bg-neutral-950">{t22}{t23}</div>;
    $[45] = t22;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  const t25 = String(timeLeft.minutes);
  let t26;
  if ($[47] !== t25) {
    t26 = t25.padStart(2, "0");
    $[47] = t25;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== t26) {
    t27 = <span className="text-foreground font-mono text-xl leading-none font-extrabold">{t26}</span>;
    $[49] = t26;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <span className="text-muted-foreground mt-1.5 text-[9px] font-bold tracking-widest uppercase dark:text-neutral-500">Mins</span>;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== t27) {
    t29 = <div className="bg-background border-border/80 flex min-w-14.5 flex-col items-center rounded-xl border px-3 py-2 shadow-xs dark:border-neutral-800/80 dark:bg-neutral-950">{t27}{t28}</div>;
    $[52] = t27;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  const t30 = String(timeLeft.seconds);
  let t31;
  if ($[54] !== t30) {
    t31 = t30.padStart(2, "0");
    $[54] = t30;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  let t32;
  if ($[56] !== t31) {
    t32 = <span className="text-foreground font-mono text-xl leading-none font-extrabold">{t31}</span>;
    $[56] = t31;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <span className="text-muted-foreground mt-1.5 text-[9px] font-bold tracking-widest uppercase dark:text-neutral-500">Secs</span>;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  let t34;
  if ($[59] !== t32) {
    t34 = <div className="bg-background border-border/80 flex min-w-14.5 flex-col items-center rounded-xl border px-3 py-2 shadow-xs dark:border-neutral-800/80 dark:bg-neutral-950">{t32}{t33}</div>;
    $[59] = t32;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== t19 || $[62] !== t24 || $[63] !== t29 || $[64] !== t34) {
    t35 = <div className="bg-muted/40 border-border/50 my-5 flex flex-col items-center rounded-2xl border p-4 backdrop-blur-xs dark:border-neutral-800/80 dark:bg-neutral-900/40">{t14}<div className="flex items-center justify-center gap-2.5">{t19}{t24}{t29}{t34}</div></div>;
    $[61] = t19;
    $[62] = t24;
    $[63] = t29;
    $[64] = t34;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  let t36;
  if ($[66] !== copied || $[67] !== handleCopyCode || $[68] !== offer.couponCode) {
    t36 = offer.couponCode && <div className="space-y-1.5"><div className="border-primary/30 dark:border-primary/40 bg-primary/5 dark:bg-primary/10 hover:bg-primary/10 dark:hover:bg-primary/15 flex items-center justify-between gap-3 rounded-xl border border-dashed p-3 transition-all duration-350"><div className="flex flex-col"><span className="text-muted-foreground text-[9px] font-bold tracking-widest uppercase dark:text-neutral-400">Promo Code</span><code className="text-primary mt-0.5 font-mono text-lg font-extrabold tracking-widest">{offer.couponCode}</code></div><Button size="sm" onClick={handleCopyCode} className="bg-primary hover:bg-primary/95 text-primary-foreground h-9 gap-1.5 rounded-lg px-4 text-xs font-semibold shadow-xs">{copied ? <><Check className="size-3.5 stroke-[2.5]" /><span>Copied</span></> : <><Copy className="size-3.5" /><span>Copy Code</span></>}</Button></div></div>;
    $[66] = copied;
    $[67] = handleCopyCode;
    $[68] = offer.couponCode;
    $[69] = t36;
  } else {
    t36 = $[69];
  }
  let t37;
  if ($[70] !== t13 || $[71] !== t35 || $[72] !== t36) {
    t37 = <div className="px-6 pt-2 pb-6">{t13}{t35}{t36}</div>;
    $[70] = t13;
    $[71] = t35;
    $[72] = t36;
    $[73] = t37;
  } else {
    t37 = $[73];
  }
  let t38;
  if ($[74] !== t10 || $[75] !== t37) {
    t38 = <DialogContent className="border-border/80 bg-card shadow-3xl overflow-hidden rounded-2xl border p-0 sm:max-w-115 dark:border-neutral-800">{t10}{t37}</DialogContent>;
    $[74] = t10;
    $[75] = t37;
    $[76] = t38;
  } else {
    t38 = $[76];
  }
  let t39;
  if ($[77] !== open || $[78] !== t38) {
    t39 = <Dialog open={open} onOpenChange={handleOpenChange}>{t38}</Dialog>;
    $[77] = open;
    $[78] = t38;
    $[79] = t39;
  } else {
    t39 = $[79];
  }
  return t39;
}